import { useQuery } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { Loader2 } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ETIQUETA_ESTATUS_POSTULACION, formatearFechaHora } from "@/lib/catalogos";
import type { EstatusPostulacion } from "@/lib/catalogos";
import { resumenAdmin } from "@/lib/postulaciones.functions";

export const Route = createFileRoute("/_authenticated/admin/")({
  component: Resumen,
});

function Resumen() {
  const { data, isLoading, error } = useQuery({
    queryKey: ["admin", "resumen"],
    queryFn: () => resumenAdmin(),
  });

  if (isLoading) return <Loader2 className="size-6 animate-spin text-primary" />;
  if (error) return <p className="text-sm text-destructive">{(error as Error).message}</p>;
  if (!data) return null;

  const indicadores = [
    { etiqueta: "Posiciones publicadas", valor: data.publicadas },
    { etiqueta: "Posiciones en borrador", valor: data.borradores },
    { etiqueta: "Postulaciones recibidas", valor: data.total },
    { etiqueta: "Nuevas por revisar", valor: data.nuevas },
    { etiqueta: "En revisión", valor: data.enRevision },
    { etiqueta: "Seleccionados", valor: data.seleccionados },
  ];

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl">Resumen general</h1>
          <p className="text-sm text-muted-foreground">
            Estado actual de la convocatoria de MEDAM.
          </p>
        </div>
        <Button asChild>
          <Link to="/admin/posiciones/$id" params={{ id: "nueva" }}>
            Nueva posición
          </Link>
        </Button>
      </div>

      <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {indicadores.map((indicador) => (
          <li
            key={indicador.etiqueta}
            className="rounded-2xl border border-border/70 bg-card p-5 shadow-sm"
          >
            <p className="text-sm text-muted-foreground">{indicador.etiqueta}</p>
            <p className="mt-1 font-display text-3xl">{indicador.valor}</p>
          </li>
        ))}
      </ul>

      <section className="rounded-2xl border border-border/70 bg-card p-5 shadow-sm">
        <h2 className="font-display text-lg">Últimas postulaciones</h2>
        {data.ultimas.length === 0 ? (
          <p className="mt-3 text-sm text-muted-foreground">Aún no hay postulaciones recibidas.</p>
        ) : (
          <ul className="mt-4 divide-y divide-border">
            {data.ultimas.map((fila) => (
              <li key={fila.id} className="flex flex-wrap items-center gap-3 py-3">
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium">
                    {fila.postulantes?.nombre_completo ?? "Sin nombre"}
                  </p>
                  <p className="truncate text-xs text-muted-foreground">
                    {fila.posiciones?.nombre} · {fila.folio} ·{" "}
                    {formatearFechaHora(fila.created_at)}
                  </p>
                </div>
                <Badge variant="secondary">
                  {ETIQUETA_ESTATUS_POSTULACION[fila.estatus as EstatusPostulacion]}
                </Badge>
                <Button asChild size="sm" variant="outline">
                  <Link to="/admin/postulaciones/$id" params={{ id: fila.id }}>
                    Ver
                  </Link>
                </Button>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
